import React, { useEffect, useRef, useState } from "react";
import {
  Bell,
  CheckCheck,
  Trash2,
  Megaphone,
  Info,
  X,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useNotifications } from "@/hooks/useNotifications";
import { Notification } from "@/types/notification";
import { cn } from "@/lib/utils";

interface NotificationsPanelProps {
  onClose: () => void;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);

  const [visibleCount, setVisibleCount] = useState(5);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 5);
  };

  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useNotifications();

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // // Infinite scroll
  // const handleScroll = useCallback(() => {
  //   const el = scrollRef.current;

  //   if (!el) return;

  //   const { scrollTop, scrollHeight, clientHeight } = el;

  //   if (scrollTop + clientHeight >= scrollHeight - 10) {
  //     setVisibleCount((prev) => {
  //       const next = prev + 5;

  //       return next > notifications.length
  //         ? notifications.length
  //         : next;
  //     });
  //   }
  // }, [notifications.length]);

  // useEffect(() => {
  //   const el = scrollRef.current;

  //   if (!el) return;

  //   el.addEventListener("scroll", handleScroll);

  //   return () => {
  //     el.removeEventListener("scroll", handleScroll);
  //   };
  // }, [handleScroll]);

  // Notification click
  const handleNotificationClick = async (
    notification: Notification
  ) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate if action URL exists
    if (notification.action_url) {
      window.location.href = notification.action_url;
      return;
    }

    // Open details dialog
    setSelectedNotification(notification);
  };

  // Notification icon
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "campaign":
        return (
          <Megaphone className="h-4 w-4 text-blue-500" />
        );

      case "system":
        return <Info className="h-4 w-4 text-gray-500" />;

      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const displayedNotifications = notifications.slice(
    0,
    visibleCount
  );

  const hasMore = visibleCount < notifications.length;

  return (
    <>
      {/* Notification Panel */}
      <div
        ref={panelRef}
        className={cn(
          // Base
          "z-[9999] overflow-hidden bg-white border border-gray-200 shadow-2xl",
          "animate-in fade-in zoom-in-95 duration-200",

          // Mobile
          "fixed top-[72px] left-3 right-3 rounded-2xl",

          // Tablet+
          "sm:absolute sm:top-14 sm:right-0 sm:left-auto sm:w-[380px]",

          // Desktop
          "lg:w-96"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2 min-w-0">
            <Bell className="h-4 w-4 text-gray-600 shrink-0" />

            <h4 className="text-sm font-semibold text-gray-900">
              Notifications
            </h4>

            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full whitespace-nowrap">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="h-8 px-2 text-xs"
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">
                  Read all
                </span>
              </Button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          ref={scrollRef}
          className="
            max-h-[70vh]
            sm:max-h-[400px]
            overflow-y-auto
            overscroll-contain
            scrollbar-thin
            scrollbar-thumb-gray-300
            scrollbar-track-transparent
          "
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-sm text-red-500">
              {error}
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />

              <p className="text-sm text-gray-500">
                No notifications yet
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {displayedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() =>
                    handleNotificationClick(notification)
                  }
                  className={cn(
                    "px-4 py-3 cursor-pointer hover:bg-gray-50 transition group",
                    !notification.is_read &&
                    "bg-blue-50/50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="mt-1 shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                        {getNotificationIcon(
                          notification.notification_type
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 leading-snug break-words">
                          {notification.title}
                        </p>

                        {!notification.is_read && (
                          <span className="h-2 w-2 bg-blue-500 rounded-full mt-1 shrink-0" />
                        )}
                      </div>

                      <div className="text-[13px] sm:text-sm text-gray-600 mt-1 space-y-1">
                        <p className="line-clamp-3 break-words">
                          {notification.message}
                        </p>

                        {notification.created_at && (
                          <p className="text-xs text-gray-500 break-words">
                            Notified on{" "}
                            {new Date(
                              notification.created_at
                            ).toLocaleString()}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 justify-between mt-2 text-xs text-gray-400">
                        <span>{notification.time_ago}</span>

                        {notification.campaign_name && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded break-all">
                            {notification.campaign_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Delete */}
                    <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Load more */}
              {/* Show More Button */}
              {hasMore && (
                <div className="px-4 py-3 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowMore}
                    className="w-full"
                  >
                    Show More
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {displayedNotifications.length} of{" "}
              {notifications.length}
            </span>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-red-600 h-8"
              onClick={clearAllNotifications}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={!!selectedNotification}
        onOpenChange={() =>
          setSelectedNotification(null)
        }
      >
        <DialogContent className="max-w-md w-[95vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="break-words">
              {selectedNotification?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm text-gray-700">
            <p className="break-words">
              {selectedNotification?.message}
            </p>

            {selectedNotification?.campaign_name && (
              <span className="inline-block bg-gray-100 px-2 py-1 rounded text-xs break-all">
                {selectedNotification.campaign_name}
              </span>
            )}

            <div className="text-xs text-gray-400">
              {selectedNotification?.time_ago}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationsPanel;
